import * as React from 'react';
import { css } from '@emotion/react';
import { useHoneycombTheme } from '../../theming';
import { FixMeLater } from '../../utils';

export type HeaderTheme = {
    height: number;
};

const headerTheme: HeaderTheme = {
    height: 56,
};

export type HeaderColor = 'default' | 'grey' | 'primary' | 'secondary' | 'tertiary';

export type HeaderProps = {
    /**
     * The content of the component.
     */
    children: React.ReactNode;

    /**
     * The component used for the root node.
     * @default header
     */
    component?: React.ElementType;

    /**
     * The colour of the component.
     * @default default
     */
    backgroundColor?: HeaderColor;

    /**
     * If `true`, the default left and right padding will not be applied.
     * @default false
     */
    disableGutters?: boolean;

    /**
     * The height of the header. By default headers have a fixed height.
     * @default fixed
     */
    height?: 'fixed' | 'fluid';

    /**
     * If `true`, the component will cast a shadow.
     * This is typically used when content is expected to scroll behind the component.
     * @default false
     */
    elevated?: boolean;
};

export const Header = React.forwardRef(function Header(
    props: HeaderProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const {
        children,
        backgroundColor: colorProp = 'default',
        component: Component = 'header',
        elevated = false,
        disableGutters = false,
        height = 'fixed',
        ...other
    } = props;

    const { theme } = useHoneycombTheme();

    const colors = React.useMemo(() => {
        const { palette } = theme;
        if (colorProp === 'default') {
            return {
                background: palette.background.content,
                text: palette.text.body,
            };
        }
        if (colorProp === 'grey') {
            return {
                background: palette.grey.lightest,
                text: palette.text.body,
            };
        }
        const brandColor = palette[colorProp];
        return {
            background: brandColor.base,
            text: brandColor.contrastText,
        };
    }, [colorProp, theme]);

    const styles = css`
        ${height === 'fixed' &&
        `
            height: ${headerTheme.height}px;
        `}
        ${height === 'fluid' &&
        `
            min-height: ${headerTheme.height}px;
            padding-top: 12px;
            padding-bottom: 12px;
        `}
        display: flex;
        align-items: center;
        background-color: ${colors.background};
        color: ${colors.text};
        ${!disableGutters && theme.fn.makeGutterStyles([{ name: 'padding-left' }, { name: 'padding-right' }])}
        ${elevated &&
        `
            box-shadow: ${theme.shadows.l};
            z-index: 1;
        `}
    `;

    return (
        <Component ref={ref} css={styles} {...other}>
            {children}
        </Component>
    );
});
